import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Input from "../input/Input";
import { useAppDispatch } from "../../app/hooks";
import {
  requestSignInEmailAsync,
  verifySignInCodeAsync,
} from "../../features/authentication/authenticationApi";
import { resolvePathAfterSignIn } from "../../features/contexts/utils/postSignIn";
import { validateEmail } from "./authValidation";

interface PasswordlessSignInFormProps {
  onBack: () => void;
}

/**
 * Passwordless sign-in: request an email carrying a magic link and a 6-digit
 * code, then either click the link or type the code here.
 */
const PasswordlessSignInForm = ({ onBack }: PasswordlessSignInFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleRequest = async (event: FormEvent) => {
    event.preventDefault();
    setEmailError(null);

    if (email.trim() === "") {
      setEmailError(t("auth.errors.emailRequired"));
      return;
    }
    if (!validateEmail(email)) {
      setEmailError(t("auth.errors.invalidEmailFormat"));
      return;
    }

    setLoading(true);
    await dispatch(requestSignInEmailAsync(email.trim()));
    setLoading(false);
    // Always advance — the API never reveals whether the email is known.
    setSent(true);
  };

  const handleVerify = async (event: FormEvent) => {
    event.preventDefault();
    setCodeError(null);

    if (code.trim().length < 6) {
      setCodeError(t("auth.passwordless.codeRequired", "Enter the 6-digit code from the email."));
      return;
    }

    setLoading(true);
    const result = await dispatch(
      verifySignInCodeAsync({ email: email.trim(), code: code.trim() })
    );
    setLoading(false);

    if (verifySignInCodeAsync.fulfilled.match(result)) {
      navigate(await resolvePathAfterSignIn(dispatch));
    } else {
      setCodeError(
        t(
          "auth.passwordless.codeInvalid",
          "That code didn't work. Check it, or request a new email."
        )
      );
    }
  };

  if (!sent) {
    return (
      <form onSubmit={handleRequest} noValidate>
        <h2 className="text-grey-20 dark:text-white">
          {t("auth.passwordless.title", "Sign in by email")}
        </h2>
        <p className="mt-2 text-grey-50 dark:text-grey-70">
          {t(
            "auth.passwordless.subtitle",
            "We'll email you a sign-in link and a code — no password needed."
          )}
        </p>

        <div className="mt-6">
          <Input
            label={t("auth.email")}
            type="email"
            name="passwordlessEmail"
            id="passwordlessEmail"
            fullWidth
            margin="dense"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            disabled={loading}
            error={!!emailError}
            helperText={emailError}
            labelPosition="top-left"
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary w-full h-[46px] mt-2 flex items-center justify-center text-[16px] no-underline"
          disabled={loading}
        >
          {t("auth.passwordless.sendEmail", "Email me a sign-in link")}
        </button>

        <div className="mt-4 text-center">
          <button
            type="button"
            className="text-sm cursor-pointer hover:underline text-primary-40 dark:text-primary-90 bg-transparent border-none"
            onClick={onBack}
          >
            {t("auth.passwordless.backToSignIn", "Back to sign in")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleVerify} noValidate>
      <h2 className="text-grey-20 dark:text-white">
        {t("auth.passwordless.sentTitle", "Check your email")}
      </h2>
      <p className="mt-2 text-grey-50 dark:text-grey-70">
        {t(
          "auth.passwordless.sentSubtitle",
          "If we know this address, an email is on its way. Click the sign-in link in it, or type the code here. Both work once and expire in 15 minutes."
        )}
      </p>

      <div className="mt-6">
        <Input
          label={t("auth.passwordless.codeLabel", "6-digit code")}
          type="text"
          name="passwordlessCode"
          id="passwordlessCode"
          fullWidth
          margin="dense"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
            if (codeError) setCodeError(null);
          }}
          disabled={loading}
          error={!!codeError}
          helperText={codeError}
          labelPosition="top-left"
        />
      </div>

      <button
        type="submit"
        className="btn btn-primary w-full h-[46px] mt-2 flex items-center justify-center text-[16px] no-underline"
        disabled={loading}
      >
        {t("auth.passwordless.verifyCode", "Sign in with code")}
      </button>

      <div className="mt-4 flex items-center justify-center gap-5 text-center">
        <button
          type="button"
          className="text-sm cursor-pointer hover:underline text-primary-40 dark:text-primary-90 bg-transparent border-none"
          onClick={() => {
            setSent(false);
            setCode("");
            setCodeError(null);
          }}
        >
          {t("auth.passwordless.useAnotherEmail", "Use another email")}
        </button>
        <button
          type="button"
          className="text-sm cursor-pointer hover:underline text-primary-40 dark:text-primary-90 bg-transparent border-none"
          onClick={onBack}
        >
          {t("auth.passwordless.backToSignIn", "Back to sign in")}
        </button>
      </div>
    </form>
  );
};

export default PasswordlessSignInForm;
